import gsap from "gsap";
import type { HistoricalTimelineDecade, HistoricalTimelineEvent } from "../generated-types";
import {
	DOT_SCATTER_MAX_DELAY_S,
	DOT_SCATTER_MAX_DURATION_S,
	DOT_SCATTER_MAX_SCALE,
	DOT_SCATTER_MIN_BLUR_PX,
	DOT_SCATTER_MIN_DURATION_S,
	DOT_SCATTER_MIN_SCALE,
	DOT_SCATTER_MAX_BLUR_PX,
	DOT_SCATTER_X_PX,
	DOT_SCATTER_Y_PX,
} from "./timeline-constants";

export function prefersReducedMotion(): boolean {
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function isRtlDocument(): boolean {
	return document.documentElement.dir === "rtl";
}

export function getHorizontalScaleOrigin(): string {
	return isRtlDocument() ? "right center" : "left center";
}

export function formatDictionaryTemplate(template: string, value: string): string {
	return template.replace("{0}", value);
}

export function escapeHtmlAttribute(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

export function getTimelineEventControlId(scopeId: string, eventIndex: number): string {
	return `historical-timeline-event-${getTimelineEventScopeKey(scopeId)}-${eventIndex}`;
}

export function getTimelineEventScopeKey(scopeId: string): string {
	const slug = scopeId
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

	if (slug) {
		return slug;
	}

	// Arabic and other non-latin scope IDs would otherwise all collapse to "year".
	let hash = 0;
	for (let index = 0; index < scopeId.length; index += 1) {
		hash = (Math.imul(31, hash) + scopeId.charCodeAt(index)) % 2_147_483_647;
	}

	return `y${hash.toString(36)}`;
}

export function normalizeTimelineYearName(yearName?: string | null): string {
	return (yearName ?? "").trim().normalize("NFC");
}

export function timelineYearNamesMatch(
	left?: string | null,
	right?: string | null
): boolean {
	const normalizedLeft = normalizeTimelineYearName(left);
	const normalizedRight = normalizeTimelineYearName(right);

	return normalizedLeft !== "" && normalizedLeft === normalizedRight;
}

export function getTimelineEventPanelId(scopeId: string, eventIndex: number): string {
	return `${getTimelineEventControlId(scopeId, eventIndex)}-panel`;
}

export function seededRandom(seed: number): number {
	const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
	return value - Math.floor(value);
}

export type DotScatterVariant = "full" | "lite";

export function getDotScatterMotion(id: number, variant: DotScatterVariant = "full") {
	const xSeed = seededRandom(id * 3 + 1);
	const ySeed = seededRandom(id * 3 + 2);
	const scaleSeed = seededRandom(id * 3 + 3);
	const delaySeed = seededRandom(id * 3 + 4);
	const durationSeed = seededRandom(id * 3 + 5);
	const blurSeed = seededRandom(id * 3 + 6);

	const duration =
		DOT_SCATTER_MIN_DURATION_S + durationSeed * (DOT_SCATTER_MAX_DURATION_S - DOT_SCATTER_MIN_DURATION_S);

	return {
		x: (xSeed - 0.5) * DOT_SCATTER_X_PX * 2,
		y: (ySeed - 0.5) * DOT_SCATTER_Y_PX * 2,
		scale: DOT_SCATTER_MIN_SCALE + scaleSeed * (DOT_SCATTER_MAX_SCALE - DOT_SCATTER_MIN_SCALE),
		blur:
			variant === "lite"
				? 0
				: DOT_SCATTER_MIN_BLUR_PX + blurSeed * (DOT_SCATTER_MAX_BLUR_PX - DOT_SCATTER_MIN_BLUR_PX),
		delay: delaySeed * DOT_SCATTER_MAX_DELAY_S,
		duration,
	};
}

export type TimelineDotItem = {
	id: number;
	yearName?: string;
	event: HistoricalTimelineEvent;
};

export function getEventTagKey(event: HistoricalTimelineEvent): string {
	const tagKey = event.tag?.key;
	if (!tagKey) {
		return "general";
	}

	return tagKey.toLowerCase().replace(/\s+/g, "-");
}

export function normalizeCategoryKey(key?: string): string {
	return (key ?? "").trim().toLowerCase();
}

export function eventMatchesCategoryKeys(
	event: HistoricalTimelineEvent,
	activeCategoryKeys: string[]
): boolean {
	if (activeCategoryKeys.length === 0) {
		return true;
	}

	const eventKey = normalizeCategoryKey(event.tag?.key);
	return eventKey !== "" && activeCategoryKeys.some((key) => normalizeCategoryKey(key) === eventKey);
}

export function getDotItems(
	decade: HistoricalTimelineDecade,
	activeCategoryKeys: string[]
): TimelineDotItem[] {
	const items: TimelineDotItem[] = [];
	let id = 0;

	for (const year of decade.years ?? []) {
		for (const event of year.events ?? []) {
			if (eventMatchesCategoryKeys(event, activeCategoryKeys)) {
				items.push({
					id: id++,
					yearName: year.name,
					event,
				});
			}
		}
	}

	return items;
}

export function getVisibleYearNames(dotItems: TimelineDotItem[]): string[] {
	const names: string[] = [];

	for (const item of dotItems) {
		if (item.yearName && !names.includes(item.yearName)) {
			names.push(item.yearName);
		}
	}

	return names;
}

export function formatYearRange(yearNames: string[]): string {
	if (yearNames.length === 0) {
		return "";
	}

	const firstYear = yearNames[0];
	const lastYear = yearNames[yearNames.length - 1] ?? firstYear;

	if (!firstYear) {
		return "";
	}

	return firstYear === lastYear ? firstYear : `${firstYear} - ${lastYear}`;
}

export function getVisibleYearRange(dotItems: TimelineDotItem[]): string {
	return formatYearRange(getVisibleYearNames(dotItems));
}

export function getDecadeYearRange(decade: HistoricalTimelineDecade): string {
	const yearNames = (decade.years ?? [])
		.map((year) => year.name?.trim())
		.filter((name): name is string => Boolean(name));

	return formatYearRange(yearNames);
}

export function getScatterMinEndMs(
	dotItems: TimelineDotItem[],
	scatterToken: number,
	scatterBaseDelay: number,
	getDotId: (dotIndex: number) => number
): number {
	return dotItems.reduce((minEndMs, _item, dotIndex) => {
		const motion = getDotScatterMotion(getDotId(dotIndex) + scatterToken * 9973);
		const endMs = (scatterBaseDelay + motion.delay + motion.duration) * 1000;
		return Math.min(minEndMs, endMs);
	}, Number.POSITIVE_INFINITY);
}

export function getEllipseProximity(
	clientX: number,
	clientY: number,
	pointX: number,
	pointY: number,
	stageRect: DOMRect,
	widthRatio: number,
	heightRatio: number,
	falloffPower: number
): number {
	const dx = pointX - clientX;
	const dy = pointY - clientY;
	const radiusX = Math.max(stageRect.width * widthRatio, 1);
	const radiusY = Math.max(stageRect.height * heightRatio, 1);
	const normalizedDistance = Math.sqrt((dx / radiusX) ** 2 + (dy / radiusY) ** 2);
	const linearProximity = gsap.utils.clamp(0, 1, 1 - normalizedDistance);

	return linearProximity ** falloffPower;
}

export function getFeaturedEvent(
	decade: HistoricalTimelineDecade,
	dotItems: TimelineDotItem[]
): HistoricalTimelineEvent | undefined {
	return dotItems[0]?.event ?? decade.years?.[0]?.events?.[0];
}

export function getValidImageUrl(url?: string): string | undefined {
	const trimmed = url?.trim();
	return trimmed ? trimmed : undefined;
}

export type TimelineEventImage = {
	imageUrl: string;
	caption?: string;
};

export function getEventImages(event?: HistoricalTimelineEvent): TimelineEventImage[] {
	return (event?.images ?? []).flatMap((image) => {
		const imageUrl = getValidImageUrl(image.imageUrl);
		if (!imageUrl) {
			return [];
		}

		const caption = image.caption?.trim();
		return [{ imageUrl, caption: caption || undefined }];
	});
}

export function getEventImageUrl(event?: HistoricalTimelineEvent): string | undefined {
	return getEventImages(event)[0]?.imageUrl;
}

export function getEventCaption(event?: HistoricalTimelineEvent): string | undefined {
	return getEventImages(event)[0]?.caption;
}

export function getDecadeHoverImageUrl(
	decade: HistoricalTimelineDecade,
	dotItems: TimelineDotItem[] = getDotItems(decade, [])
): string | undefined {
	const decadeImage = getValidImageUrl(decade.image);
	if (decadeImage) {
		return decadeImage;
	}

	const featuredEvent = getFeaturedEvent(decade, dotItems);
	return getEventImageUrl(featuredEvent);
}

export function sanitizeTagModifier(key?: string): string {
	if (!key) {
		return "general";
	}

	return key.toLowerCase().replace(/[^a-z0-9-]/g, "-");
}

export type YearGroup = {
	yearName: string;
	dotItems: TimelineDotItem[];
};

export function getYearGroups(
	decade: HistoricalTimelineDecade,
	activeCategoryKeys: string[]
): YearGroup[] {
	const groups: YearGroup[] = [];
	const indexByName = new Map<string, number>();
	let id = 0;

	for (const year of decade.years ?? []) {
		const yearName = year.name ?? "";
		const matchingEvents = (year.events ?? []).filter((event) =>
			eventMatchesCategoryKeys(event, activeCategoryKeys)
		);

		if (matchingEvents.length === 0) {
			continue;
		}

		for (const event of matchingEvents) {
			const dotItem: TimelineDotItem = {
				id: id++,
				yearName,
				event,
			};

			const existingIndex = yearName ? indexByName.get(yearName) : undefined;

			if (existingIndex !== undefined) {
				groups[existingIndex].dotItems.push(dotItem);
				continue;
			}

			indexByName.set(yearName || String(groups.length), groups.length);
			groups.push({
				yearName,
				dotItems: [dotItem],
			});
		}
	}

	return groups;
}
